<!-- Component for displaying a dropdown of products that can be selected based on tags -->
<template>
	<div class="relative w-full">
		<!-- Dropdown Trigger -->
		<div
			class="group hover:border-primary-700 hover:text-primary-700 flex cursor-pointer items-center justify-between rounded-md border bg-white p-3"
			@click="toggleDropdown"
			:class="
				isDropdownOpen
					? 'border-primary-700 text-primary-700'
					: 'border-slate-300'
			"
		>
			{{ selectedFilterOption?.name }}
			<PlayIcon
				class="group-hover:text-primary-700 h-4 w-4 text-xs transition-transform duration-300"
				:class="
					isDropdownOpen
						? 'text-primary-700 rotate-90'
						: '-rotate-90 text-slate-500'
				"
			/>
		</div>

		<!-- Dropdown Menu -->
		<div
			v-if="isDropdownOpen"
			class="absolute left-0 z-50 mt-2 w-full rounded-md border border-slate-300 bg-white shadow-lg"
		>
			<div
				v-for="tag in filterOptions"
				:key="tag.id"
				class="cursor-pointer p-3 hover:bg-slate-100"
				@click="selectFilterOption(tag)"
			>
				{{ tag.name }}
			</div>
		</div>

		<!-- Product List -->
		<div
			v-if="filteredProducts?.length"
			class="mt-1 rounded-md bg-white"
		>
			<div
				v-for="p in filteredProducts"
				:key="p.id"
				class="group flex cursor-pointer flex-col border-b border-slate-200 py-4 last:border-b-0"
				@click="selectedProduct = p"
			>
				<BlobImage
					class="aspect-16/11 w-full rounded-xs object-cover"
					:src="p.productImageUrl ?? undefined"
					:alt="p.name"
				/>

				<div class="flex min-h-[48px] w-full items-center justify-between pt-4">
					<span
						class="group-hover:text-accent-600 group-hover:font-semibold"
						:class="
							selectedProduct?.id === p.id
								? 'text-accent-600 font-semibold'
								: 'text-secondary-900'
						"
					>
						{{ p.name }}
					</span>
					<PlanAvatarList
						:plans="productPlans[p.id]"
						filled
					/>
				</div>
			</div>
		</div>

		<div
			v-else
			class="pt-4 text-center text-slate-500"
		>
			{{ t('productCategories.noProducts') }}
		</div>
	</div>
</template>

<script setup lang="ts">
import BlobImage from '../common/BlobImage.vue'
import PlanAvatarList from '../presentation/PlanAvatarList.vue'
import type { PbLineItem } from '@/apiTypes'
import type { PbLineItemProductAndPricingOptions } from '@/classes/PriceBook'
import { PbSubcategory } from '@/enums/PbSubcategory'
import { usePresentationLocale } from '@/i18n'
import { useMerchandise } from '@/mixins/merchandise'
import { PlayIcon } from '@heroicons/vue/24/solid'
import { all } from 'axios'
import { ref, onMounted, type PropType, computed, watch } from 'vue'

const { priceBook, doesLineItemHaveTag } = useMerchandise()
const { t } = usePresentationLocale()

type ProductType = 'urn' | 'casket' | 'vault'

const props = defineProps({
	productPlans: {
		type: Object,
		required: true,
	},
	productType: {
		type: String as PropType<ProductType>,
		required: true,
		default: 'urn',
	},
})

type Product = Pick<
	PbLineItemProductAndPricingOptions,
	'id' | 'name' | 'productImageUrl'
>

/** A filter that can be selected in the drop-down */
interface FilterOption {
	id: string

	/** Label to display to the user */
	name: string

	/** Filter to apply to the products */
	filter: (product: PbLineItemProductAndPricingOptions) => boolean
}

const selectedProduct = defineModel<Product>()

const isDropdownOpen = ref(false)
const selectedFilterOption = ref<FilterOption>()

/** Filter function for all the products we have access to */
const productFilter = computed(() => {
	if (props.productType === 'urn') {
		return (product: PbLineItem) =>
			product.subcategory === PbSubcategory.MerchandiseUrn && !product.isStandIn
	}

	if (props.productType === 'casket') {
		return (product: PbLineItem) =>
			product.subcategory === PbSubcategory.MerchandiseCasket &&
			!product.isStandIn
	}

	return (product: PbLineItem) =>
		product.subcategory === PbSubcategory.MerchandiseOuterBurialContainer &&
		!product.isStandIn
})

/** All the products that can be viewed on this page */
const allProducts = computed<PbLineItemProductAndPricingOptions[]>(() => {
	if (!priceBook.value?.products) return []

	return priceBook.value.products.filter(productFilter.value)
})

/** The products filtered by the currently-selected filter sorted by price descending */
const filteredProducts = computed<PbLineItemProductAndPricingOptions[]>(() => {
	if (!priceBook.value || !priceBook.value.products) return []

	if (!selectedFilterOption.value) return allProducts.value ?? []

	return allProducts.value
		.filter(selectedFilterOption.value.filter)
		.sort((a, b) => b?.fixedPrice - a?.fixedPrice)
})

/** All the filter options for the drop-down */
const filterOptions = computed<FilterOption[]>(() => {
	const productTags =
		priceBook.value?.getProductTags(productFilter.value).map((sfTag) => ({
			...sfTag,
			filter: (product: PbLineItemProductAndPricingOptions) =>
				doesLineItemHaveTag(product, sfTag.id),
		})) ?? []

	// Find the "popular" tag from productTags
	const popularTag = productTags.find(
		(tag) => tag.name?.toLowerCase() === 'popular'
	)

	// Conditionally define the popular option if any products exist with the "popular" tag
	const popularTagOption: FilterOption[] = popularTag
		? [
				{
					id: 'popular',
					name: t('productTags.popular'),
					filter: (product) => doesLineItemHaveTag(product, popularTag.id),
				},
			]
		: []

	// Define "All" filter
	const allTagOption: FilterOption = {
		id: 'all',
		name: t('general.all'),
		filter: () => true,
	}

	// Compile the final list: Popular (if it exists) -> All -> Sorted remaining
	return [
		...popularTagOption,
		allTagOption,
		...productTags
			.filter((tag) => tag.id !== popularTag?.id)
			.sort((a, b) => a.name.localeCompare(b.name)),
	]
})

// Toggle the dropdown menu
const toggleDropdown = () => {
	isDropdownOpen.value = !isDropdownOpen.value
}

// Select a tag and filter products
const selectFilterOption = (option: FilterOption) => {
	selectedFilterOption.value = option
	isDropdownOpen.value = false
}

// On mount auto-select first tag
onMounted(() => {
	if (filterOptions.value.length > 0) {
		selectFilterOption(filterOptions.value[0])
	}
})

/** When the shown list of products changes, select the first one */
watch(
	filteredProducts,
	(newProducts) => {
		if (newProducts.length > 0) {
			selectedProduct.value = newProducts[0]
		}
	},
	{ immediate: true }
)
</script>
